import {
  DataTableSkeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { groupBy, uniq } from 'lodash-es';
import { CategorySlotCount } from 'types/evaluation';
import { parseMomentUtc, US_DATE_FORMAT } from 'utils/dates';
import { FormattedCount } from 'components/formatted-count/formatted-count';
import {
  shownSlotCategoriesIn,
  SLOT_CATEGORY_LABELS,
} from 'utils/slot-categories';
import style from './rescat-slot-count-table.module.scss';

interface RescatSlotCountTableProps {
  slotCount: CategorySlotCount[];
  isLoading: boolean;
}

export const RescatSlotCountTable = ({
  slotCount,
  isLoading,
}: RescatSlotCountTableProps) => {
  const sortedDates = uniq(slotCount.map((item) => item.date)).sort((a, b) =>
    parseMomentUtc(a).diff(parseMomentUtc(b))
  );

  const headers =
    sortedDates.length === 0 ? Array(8).fill('mm/dd/yyy') : sortedDates;

  const groupedByDate = groupBy(slotCount, 'date');

  /**
   * The slot count for a given date + reserve category, or undefined when absent.
   * @param {string} date - The interval date key.
   * @param {string} category - The `rollForwardSlotCat` to look up.
   * @return {number | undefined} The slot count, or undefined if not present.
   */
  const countFor = (date: string, category: string) =>
    groupedByDate[date]?.find(
      (slotConversion) => slotConversion.rollForwardSlotCat === category
    )?.slotCount;

  if (isLoading) {
    return (
      <DataTableSkeleton
        data-testid="data-table-skeleton"
        showHeader={false}
        showToolbar={false}
        columnCount={4}
        rowCount={5}
      />
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeader>Reserves Types</TableHeader>
            {headers.map((date, index) => (
              <TableHeader key={`date-${index}`}>
                {sortedDates.length
                  ? parseMomentUtc(date).format(US_DATE_FORMAT)
                  : date}
              </TableHeader>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {shownSlotCategoriesIn(
            slotCount,
            (item) => item.rollForwardSlotCat,
            (item) => item.slotCount
          ).map((category) => (
            <TableRow key={category}>
              <TableCell>{SLOT_CATEGORY_LABELS[category]}</TableCell>
              {headers.map((date, index) => (
                <TableCell key={`date-${index}`}>
                  <FormattedCount value={countFor(date, category)} />
                </TableCell>
              ))}
            </TableRow>
          ))}
          <TableRow>
            <TableCell>Shut</TableCell>
            {headers.map((date, index) => (
              <TableCell key={`date-${index}`}>
                <FormattedCount value={countFor(date, 'shut_in')} />
              </TableCell>
            ))}
          </TableRow>
          <TableRow className={style.highlight}>
            <TableCell>Total</TableCell>
            {headers.map((date, index) => (
              <TableCell key={`date-${index}`}>
                <FormattedCount value={countFor(date, 'total')} />
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};
